import type {
  AddressValidation,
  SuggestedAddress
} from '@ecomm/data-simplisafe-api'
import { noValue } from '@ecomm/utils'
import type { Address } from '@simplisafe/eis-commercetools-carts'
import type { Locale } from '@simplisafe/ewok'
import * as A from 'fp-ts/lib/Array'
import * as O from 'fp-ts/lib/Option'
import { pipe } from 'fp-ts/lib/function'

export enum AddressCorrections {
  ChangedAddressComponent = 'ChangedAddressComponent',
  CorrectedCityStateSpelling = 'CorrectedCityStateSpelling',
  CorrectedStreetSpelling = 'CorrectedStreetSpelling',
  CorrectedZip = 'CorrectedZip',
  FixedAbbreviations = 'FixedAbbreviations',
  UsedAddresseeData = 'UsedAddresseeData',
  InsufficientIncorrectAddressData = 'InsufficientIncorrectAddressData',
  BetterAddressExists = 'BetterAddressExists',
  UnofficialCityName = 'UnofficialCityName'
}

export enum AddressFootnotes {
  NoPlus4Assigned = 'NoPlus4Assigned',
  MissingSecondaryNumber = 'MissingSecondaryNumber',
  UnrecognizedSecondaryAddress = 'UnrecognizedSecondaryAddress',
  InvalidCityStateZip = 'InvalidCityStateZip',
  SameZipForMultiple = 'SameZipForMultiple',
  AddressNotFound = 'AddressNotFound',
  DualAddress = 'DualAddress',
  CardinalRuleMatch = 'CardinalRuleMatch',
  FlaggedAddressForLACSLink = 'FlaggedAddressForLACSLink',
  MultipleZipPlus4 = 'MultipleZipPlus4',
  UniqueZipMatch = 'UniqueZipMatch',
  NoMatchEWSMatchSoon = 'NoMatchEWSMatchSoon',
  MagnetStreetSyndrome = 'MagnetStreetSyndrome',
  UnverifiableCityState = 'UnverifiableCityState',
  InvalidDeliveryAddress = 'InvalidDeliveryAddress',
  UniqueZipCode = 'UniqueZipCode',
  MilitaryMatch = 'MilitaryMatch',
  MatchedWithZipMove = 'MatchedWithZipMove'
}

export const isAddressValidationSupported = (locale: Locale) =>
  locale === 'en-US'

export const getFirstSuggestedAddress = (
  addressValidation: AddressValidation | null
): SuggestedAddress => {
  return pipe(
    O.fromNullable(addressValidation?.suggestions),
    O.chain(A.head),
    O.chain(suggestion => O.fromNullable(suggestion.address)),
    O.getOrElse(() => ({}))
  )
}

export const hasAddressSuggestions = (
  addressValidation: AddressValidation
): boolean => {
  return (
    addressValidation.foundValidAddress &&
    !!addressValidation.suggestions?.length
  )
}

export const hasAddressCorrections = (
  addressValidation: AddressValidation
): boolean => {
  return pipe(
    O.fromNullable(addressValidation.suggestions),
    O.chain(A.head),
    O.chain(suggestion => O.fromNullable(suggestion.corrections)),
    O.fold(
      () => false,
      corrections => corrections.length > 0
    )
  )
}

export const hasInvalidAddressCorrections = (
  addressValidation: AddressValidation
): boolean => {
  return pipe(
    O.fromNullable(addressValidation.suggestions),
    O.chain(A.head),
    O.chain(suggestion => O.fromNullable(suggestion.corrections)),
    O.fold(
      () => false,
      corrections =>
        corrections.length > 0 &&
        corrections.every(c => c === AddressCorrections.FixedAbbreviations)
    )
  )
}

/**
 * Determines if the shipping address validation modal should render
 *
 * The modal should appear if:
 *
 *   1. The user is in the US.
 *   2. The API found a correction in the entered address.
 *   3. Corrections array is not empty
 *   4. Corrections array does not have an invalid correction (only FixedAbbreviations for now)
 */
export const shouldRenderShippingAddressModal = (
  locale: Locale,
  addressValidation: AddressValidation
) => {
  return (
    isAddressValidationSupported(locale) &&
    hasAddressSuggestions(addressValidation) &&
    hasAddressCorrections(addressValidation) &&
    !hasInvalidAddressCorrections(addressValidation)
  )
}

/**
 * Determines if the we need to silently update the user address -> without opening the modal
 *
 * Should be true if:
 *
 *   1. The user is in the US.
 *   2. The API found a correction in the entered address.
 *   3. Corrections array is empty or only contains an invalid correction (only FixedAbbreviations for now)
 */
export const shouldSilentlyUpdateAddress = (
  locale: Locale,
  addressValidation: AddressValidation
) => {
  return (
    isAddressValidationSupported(locale) &&
    hasAddressSuggestions(addressValidation) &&
    (!hasAddressCorrections(addressValidation) ||
      hasInvalidAddressCorrections(addressValidation))
  )
}

export const combineCartAndSuggestedAddress = (
  locale: Locale,
  cartAddress: Address | null,
  suggestedAddress: SuggestedAddress
): Address => ({
  firstName: cartAddress?.firstName,
  lastName: cartAddress?.lastName,
  email: cartAddress?.email,
  phone: cartAddress?.phone,
  country: locale === 'en-US' ? 'US' : 'GB',
  city: suggestedAddress.city,
  state: locale === 'en-US' ? suggestedAddress.state : noValue(),
  streetName: suggestedAddress.street,
  postalCode: suggestedAddress.plus4Code
    ? `${suggestedAddress.zipcode}-${suggestedAddress.plus4Code}`
    : suggestedAddress.zipcode,
  additionalStreetInfo: suggestedAddress.additionalStreetInfo || ''
})
